import { useLocation } from "@tanstack/react-router";
import { cn } from "tailwind-variants";
import type {
	ApiBlockGridModel,
	ApiBlockItemModel,
	MegaMenuItemUmbBlockPartialElementModel,
	MegaMenuUmbBlockElementModel,
	MegaMenuUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { MegaMenuItemUmbBlockPartialElementModelContentType } from "#/_generated/umbraco/delivery-api/models";
import {
	MegaMenu,
	type MegaMenuContainerType,
	type MegaMenuItem,
	type MegaMenuWidth,
} from "#/_packages/umbrella/features/mega-menu/components/mega-menu.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";

type MegaMenuBlockProps = BlockItemProps & {
	content: MegaMenuUmbBlockElementModel;
	settings: MegaMenuUmbBlockSettingsElementModel;
};

function isMegaMenuItemBlock(
	item: ApiBlockItemModel,
): item is ApiBlockItemModel & {
	content: MegaMenuItemUmbBlockPartialElementModel;
} {
	return (
		item.content.contentType ===
		MegaMenuItemUmbBlockPartialElementModelContentType.megaMenuItemUmbBlockPartial
	);
}

function tryGetContainerType(value?: string | null): MegaMenuContainerType {
	return value === "text" ? "text" : "link";
}

function tryGetWidth(value?: string | null): MegaMenuWidth {
	return value === "normal" || value === "wide" ? value : "narrow";
}

function hasBlockGridItems(contentArea?: ApiBlockGridModel): boolean {
	return Boolean(contentArea?.items && contentArea.items.length > 0);
}

function mapMegaMenuItem(
	item: ApiBlockItemModel,
	location: string,
): MegaMenuItem | null {
	if (!isMegaMenuItemBlock(item)) {
		return null;
	}

	const properties = item.content.properties;
	const link = getUmbLink(properties?.link?.[0], {
		id: item.content.id,
		requireHref: true,
	});
	const containerType = tryGetContainerType(properties?.typeOfContainer);
	const label =
		containerType === "link"
			? (link?.title ?? properties?.label)
			: properties?.label;

	if (!label) {
		return null;
	}

	const contentArea = properties?.contentArea;
	const linkPath =
		link && !link.isExternal ? link.href.split(/[?#]/, 1)[0] : undefined;

	return {
		id: item.content.id,
		containerType,
		label,
		isActive: linkPath ? location.includes(linkPath) : false,
		link,
		width: tryGetWidth(properties?.width),
		noVerticalSpacing: properties?.noVerticalSpacing ?? false,
		content: hasBlockGridItems(contentArea) ? contentArea?.items : undefined,
	};
}

export default function MegaMenuUmbBlock({
	className,
	content,
	settings,
}: MegaMenuBlockProps) {
	const location = useLocation();

	const items =
		content.properties?.contentArea?.items
			.map((item) =>
				mapMegaMenuItem(item as ApiBlockItemModel, location.pathname),
			)
			.filter((item): item is MegaMenuItem => item !== null) ?? [];

	return (
		<MegaMenu
			id={settings.properties?.generatedId ?? undefined}
			items={items}
			hoverShow={content.properties?.hoverShow ?? false}
			className={cn(className)}
		/>
	);
}
